import React from "react";

function format(n: number) {
  return Intl.NumberFormat().format(n);
}

/**
 * Maps the legacy solid `color` prop (e.g. "bg-sky-500") to a refined, muted
 * icon-tile treatment. Keeping the same prop means existing call sites don't
 * change while the look becomes more enterprise-grade (soft tint instead of a
 * saturated block of colour).
 *
 * Classes are written out in full so Tailwind's compiler can see them.
 */
const TONE_MAP: Record<string, { tile: string; bar: string }> = {
  "bg-sky-500": { tile: "bg-sky-50 text-sky-600 ring-sky-100", bar: "bg-sky-500" },
  "bg-green-500": { tile: "bg-emerald-50 text-emerald-600 ring-emerald-100", bar: "bg-emerald-500" },
  "bg-emerald-500": { tile: "bg-emerald-50 text-emerald-600 ring-emerald-100", bar: "bg-emerald-500" },
  "bg-red-500": { tile: "bg-rose-50 text-rose-600 ring-rose-100", bar: "bg-rose-500" },
  "bg-rose-500": { tile: "bg-rose-50 text-rose-600 ring-rose-100", bar: "bg-rose-500" },
  "bg-indigo-500": { tile: "bg-indigo-50 text-indigo-600 ring-indigo-100", bar: "bg-indigo-500" },
  "bg-violet-500": { tile: "bg-violet-50 text-violet-600 ring-violet-100", bar: "bg-violet-500" },
  "bg-amber-500": { tile: "bg-amber-50 text-amber-600 ring-amber-100", bar: "bg-amber-500" },
};

const FALLBACK_TONE = { tile: "bg-zinc-100 text-zinc-600 ring-zinc-200", bar: "bg-zinc-400" };

export default function KpiCard({
  title,
  value,
  icon,
  color,
  hint,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  /** Legacy tailwind bg class e.g. "bg-sky-500" — mapped to a muted tone. */
  color: string;
  /** Optional small caption under the value (e.g. "Active accounts"). */
  hint?: string;
}) {
  const tone = TONE_MAP[color] ?? FALLBACK_TONE;
  const display = typeof value === "number" ? format(value) : value;

  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white p-4 transition-colors duration-200 hover:border-zinc-300 hover:shadow-sm sm:p-5">
      {/* left accent bar */}
      <span
        className={`absolute inset-y-0 left-0 w-1 ${tone.bar} opacity-0 transition-opacity duration-200 group-hover:opacity-100`}
        aria-hidden
      />

      {/* Header row: label + icon. Label is allowed to wrap (no truncation). */}
      <div className="flex items-start justify-between gap-2">
        <div
          className="text-[11px] font-medium uppercase leading-tight tracking-wider text-zinc-500"
          title={title}
        >
          {title}
        </div>

        <div
          className={[
            "grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1",
            tone.tile,
          ].join(" ")}
        >
          <span className="text-base">{icon}</span>
        </div>
      </div>

      {/* Value pinned to the bottom so cards align regardless of label height. */}
      <div className="mt-auto pt-3">
        <div
          className="text-2xl font-semibold leading-none text-zinc-900 tabular-nums"
          title={typeof value === "number" ? format(value) : String(value)}
        >
          {display}
        </div>
        {hint ? (
          <div className="mt-1.5 text-xs text-zinc-400" title={hint}>
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );
}
